import type {
  Achievement,
  ServerConfig,
  ServerConfigProductCustomAttribute
} from 'shared-types';

type CustomAttributesByKey = {
  [key: string]: ServerConfigProductCustomAttribute;
};
type CustomAttributeType =
  | Achievement['type']
  | 'achievement'
  | 'calendarEvent'
  | 'course'
  | 'individualAchievement'
  | 'path';

interface Params {
  excludeKeys?: string[];
  serverConfig: ServerConfig<'guest'>;
  type?: CustomAttributeType;
}

// overloads
function availableCustomAttributesByType({
  excludeKeys,
  serverConfig,
  type
}: {
  excludeKeys?: never;
  serverConfig: ServerConfig<'guest'>;
  type: CustomAttributeType;
}): ServerConfigProductCustomAttribute[];
function availableCustomAttributesByType({
  serverConfig,
  type
}: {
  excludeKeys?: string[];
  serverConfig: ServerConfig<'guest'>;
  type?: never;
}): CustomAttributesByKey;

// implementation
function availableCustomAttributesByType({
  excludeKeys = [],
  serverConfig,
  type
}: Params) {
  const customAttributesByType = {
    conversation: serverConfig.achievementConversationCustomAttributes,
    external: serverConfig.achievementExternalCustomAttributes,
    'group-conversation':
      serverConfig.achievementGroupConversationCustomAttributes,
    milestone: serverConfig.achievementMilestoneCustomAttributes,
    tasklist: serverConfig.achievementTasklistCustomAttributes,
    calendarEvent: serverConfig.calendarEventCustomAttributes,
    course: serverConfig.courseCustomAttributes,
    individualAchievement: serverConfig.individualAchievementCustomAttributes,
    path: serverConfig.pathCustomAttributes
  };

  return type === 'achievement'
    ? [
        ...serverConfig.achievementConversationCustomAttributes,
        ...serverConfig.achievementExternalCustomAttributes,
        ...serverConfig.achievementGroupConversationCustomAttributes,
        ...serverConfig.achievementMilestoneCustomAttributes,
        ...serverConfig.achievementTasklistCustomAttributes
      ].reduce((uniqueAttr, thisAttr) => {
        const isUnique = !uniqueAttr.find((attr) => attr.key === thisAttr.key);
        if (isUnique) uniqueAttr.push(thisAttr);

        return uniqueAttr;
      }, [] as ServerConfigProductCustomAttribute[])
    : type
    ? customAttributesByType[type]
    : Object.keys(customAttributesByType).reduce((allAttributes, type) => {
        const attributes = customAttributesByType[type as CustomAttributeType];
        attributes.forEach((attr) => {
          if (excludeKeys.includes(attr.key)) return;
          allAttributes[attr.key] = attr;
        });

        return allAttributes;
      }, {} as CustomAttributesByKey);
}

export default availableCustomAttributesByType;
